import React, { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import styles from './ItemsPage.module.css';
import itemService from '../services/itemService';
import { Link } from 'react-router-dom';
import { ItemModel } from '../models/ItemModel';
import ConfirmDialogueComp from '../components/ConfirmDialogueComp';
import { ScrollableRefProps } from '../App';
import { useAppContext } from '../context/AppContext';
import { formatPrice } from '../utils/currencyUtils';
import { useReferenceDataContext } from '../context/ReferenceDataContext';
import { useCategorySelector } from '../hooks/useItemCategorySelector';
import { set } from 'date-fns';
import PaginationComp from '../components/PaginationComp';


const ItemsPage = ({ scrollContainerRef }: ScrollableRefProps) => {
    const { sellerAccount } = useAppContext();
    const { categories } = useReferenceDataContext();//categories for filtering items
    const { getCategoryNameById } = useCategorySelector(categories ?? []);

    const [items, setItems] = useState<ItemModel[]>([]);
    const [itemIdToDelete, setItemIdToDelete] = useState<number | null>(null);
    const [confirmDeleteModalVisible, setConfirmDeleteModalVisible] = useState(false);
    const [serviceError, setServiceError] = useState(''); //error for service call
    const [pageNum, setPageNum] = useState(0); //current page number
    const [totalItems, setTotalItems] = useState(0);
    const pageSize = 10; //number of items per page
    const [categoryId, setCategoryId] = useState(-1); //category id for filtering items
    const [keywords, setKeywords] = useState(''); //keywords for searching items  
    const itemImageURL = process.env.REACT_APP_ITEM_IMAGE_URL;
    const [imageLoadingMap, setImageLoadingMap] = useState<{ [key: number]: boolean }>({}); // Map to track loading state of images

    const [isDeleting, setIsDeleting] = useState(false); //loading state for deleting item
    const [isLoading, setIsLoading] = useState(true); //loading state for loading items

    const [hasResetFilter, setHasResetFilter] = useState(false);
    const isFilterDisabled = useMemo(() => {
        return !keywords && categoryId === -1 && !isLoading && items.length === 0;
    }, [keywords, categoryId, isLoading, items.length]);
    const loadItems = async () => {
        setIsLoading(true);
        try {

            const response = await itemService.searchItems(categoryId, keywords, pageNum * pageSize, pageSize)
            setItems(response.items);
            setTotalItems(response.itemCount);
            setImageLoadingMap(prev => {
                const updatedMap = { ...prev };
                response.items.forEach(item => {
                    if (!(item.itemId in prev)) {
                        updatedMap[item.itemId] = true;
                    }
                });
                return updatedMap;
            });
        }
        catch (error) {
            setServiceError('Failed to load items. Please try again later.');
        } finally {
            setIsLoading(false); // Set loading state to false
            setHasResetFilter(false); // Set hasSearched to true after loading items
        }
    }


    useEffect(() => {
        const delayDebounce = setTimeout(() => {

            loadItems(); // Reload items after a delay
        }, 500); // Adjust delay time as needed (500 ms)

        return () => {
            clearTimeout(delayDebounce); // Clear previous timeout if keywords change
        };
    }, [keywords, categoryId, pageNum]);




    useLayoutEffect(() => {
        if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollTo({ top: 0, behavior: 'smooth' });
        }
    }, [items, pageNum, scrollContainerRef]);

    const changePage = (newPageNum: number) => {
        setPageNum(newPageNum);
    };



    const handleResetFilter = () => {
        setCategoryId(-1);
        setKeywords('');
        setPageNum(0);
        setHasResetFilter(true);
    }

    const handleDeleteItem = (itemId: number) => {
        if (itemId) {
            setItemIdToDelete(itemId); // Set the item ID
            setConfirmDeleteModalVisible(true);
        }
    }

    const handleDelete = () => {
        const deleteItem = async () => {
            try {
                if (itemIdToDelete !== null) {
                    const deletedSucceed = await itemService.deleteItem(itemIdToDelete);
                    if (deletedSucceed === "Success") {
                        loadItems(); // Reload items after deletion
                    }
                    else {
                        setServiceError(`Failed to delete item. Please try again.`);
                    }
                }

            }
            catch (error) {
                setServiceError(`Failed to delete item. Please try again.`);
            }
            finally {
                setItemIdToDelete(null);
                setConfirmDeleteModalVisible(false); // Hide modal
                setIsDeleting(false); // Reset loading state
            }
        }

        setIsDeleting(true); // Set loading state to true
        deleteItem();


    };

    const handleCancel = () => {

        setConfirmDeleteModalVisible(false); // Hide the modal on cancel
    };

    return (
        <div className='mt-3'>
            <div className='row mb-5'>
                <div className='col-12'>
                    <div className="card rounded-0">
                        <div className="card-body p-4">
                            <h5>Items</h5>
                            {/* Alert if email is not verified */}
                            {!sellerAccount?.emailVerified && (
                                <div className="alert alert-warning mt-3" role="alert">
                                    Your email is not verified. You cannot add new items until you verify your account. <br />Go to <Link to="/myAccount" className='text-reset'>My Account</Link> under your profile to verify your email.
                                </div>
                            )}
                            <div className='row mt-3'>
                                <div className='col-md-5 mt-2'>
                                    {sellerAccount?.emailVerified ? (
                                        <Link to="/items/add" state={{ categories }} className="btn btn-success rounded-0">Add New Item</Link>
                                    ) : (
                                        <button className="btn btn-success rounded-0" disabled>Add New Item</button>
                                    )}
                                </div>
                                <div className='col-md-4 mt-2'>
                                    {/* Search bar */}
                                    <div className="input-group">
                                        <span className={` input-group-text ${isFilterDisabled ? '' : styles.itemSearchIcon}`}>
                                            <i className="bi bi-search"></i>
                                        </span>
                                        <input
                                            type="text"
                                            className={`form-control ${styles.itemSearchInput}`}
                                            placeholder="Search by keywords"
                                            aria-label="Search items"
                                            aria-describedby="search-items"
                                            value={keywords}
                                            onChange={(e) => {
                                                setKeywords(e.target.value);
                                                setPageNum(0);
                                            }}
                                            disabled={isFilterDisabled}
                                        />
                                    </div>
                                </div>
                                <div className='col-md-3 mt-2'>
                                    <div className="d-flex">
                                        {/* Filter */}
                                        <select
                                            id="categoryFilter"
                                            className="form-select"
                                            onChange={(e) => {
                                                setCategoryId(Number(e.target.value));
                                                setPageNum(0);
                                            }}
                                            value={categoryId === -1 ? "" : categoryId}
                                            disabled={isFilterDisabled}
                                        >
                                            <option value="">Filter by Category</option>

                                            {(categories ?? []).map((parentCategory) => (
                                                <React.Fragment key={parentCategory.id}>
                                                    {/* Root category */}
                                                    <option value={parentCategory.id} className="fw-bold">
                                                        {parentCategory.name}
                                                    </option>

                                                    {/* Children */}
                                                    {parentCategory.children?.map((child) => (
                                                        <option key={child.id} value={child.id}>
                                                            &nbsp;&nbsp;{child.name} {/* Indent child visually */}
                                                        </option>
                                                    ))}
                                                </React.Fragment>
                                            ))}
                                        </select>

                                        <button
                                            type="button"
                                            title="Reset Filter"
                                            className="btn btn-outline-dark rounded-0 ms-4"
                                            onClick={handleResetFilter}
                                            disabled={isFilterDisabled}
                                        >
                                            Reset
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="col-md-12 mt-3">
                    {isLoading && (
                        <div className="text-center gap-2 mt-3 text-secondary">
                            <div className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></div>
                            <span>  Searching...</span>
                        </div>
                    )}
                    {/* Empty results */}
                    {!isLoading && items.length === 0 && (
                        (keywords !== '' || categoryId !== -1) ? (
                            <div className="border fs-4 mt-4 p-5 text-center">
                                <i className="bi bi-stars clearfix"></i>
                                <span className='clearfix'>There are currently no items matching your search criteria.</span>
                                <span>Try different keywords or filter.</span>
                            </div>
                        ) : (
                            !hasResetFilter && (<div className="border fs-4 mt-4 p-5 text-center">
                                <i className="bi bi-stars clearfix"></i>
                                <span className='clearfix'>We couldn't find any items in your store.</span>
                                {sellerAccount?.emailVerified && (
                                    <span>
                                        <Link to="/items/add" state={{ categories }} className="btn btn-outline-dark rounded-0 mt-3">Create New</Link>
                                    </span>
                                )}
                            </div>)
                        )
                    )}
                    {/* Items list */}
                    {!isLoading && items.map(item => (
                        <div key={item.itemId} className="card mb-3 rounded-0">
                            {item.reviewState === "Pending" && (
                                <div className={`alert alert-info text-center rounded-0 mb-0 ${styles.reviewAlert}`} role="alert">
                                    This item is currently under review. It will be listed once it is approved.
                                </div>
                            )}

                            {item.reviewState === "Rejected" && (
                                <div className={`alert alert-danger text-center rounded-0 mb-0 ${styles.reviewAlert}`} role="alert">
                                    This item has been rejected. {(item.reviewComment === "" || item.reviewComment === null) ? "" : `Reason: ${item.reviewComment}`}
                                </div>
                            )}
                            <div className="row g-0 align-items-stretch">
                                <div className="position-relative col-md-2 text-center p-4">
                                    {/* Loading */}
                                    {imageLoadingMap[item.itemId] && (
                                        <div className="position-absolute top-50 start-50 translate-middle">
                                            <div className="spinner-border text-secondary" role="status" style={{ width: '2rem', height: '2rem' }}>
                                                <span className="visually-hidden">Loading...</span>
                                            </div>
                                        </div>
                                    )}
                                    {/* Image */}
                                    <img
                                        src={item.primaryImage ? `${itemImageURL}/${item.primaryImage}` : "/images/fallback.png"}
                                        alt={item.itemName}
                                        className="img-fluid rounded-start border"
                                        style={{ height: '168px', width: '168px', objectFit: 'contain', visibility: imageLoadingMap[item.itemId] ? 'hidden' : 'visible' }}
                                        onLoad={() => setImageLoadingMap(prev => ({ ...prev, [item.itemId]: false }))}
                                        onError={(e) => {
                                            const target = e.target as HTMLImageElement;
                                            target.onerror = null;
                                            target.src = "/images/fallback.png";
                                            setImageLoadingMap(prev => ({ ...prev, [item.itemId]: false }));
                                        }}
                                        onLoadStart={() => setImageLoadingMap(prev => ({ ...prev, [item.itemId]: true }))}
                                    />
                                </div>
                                <div className="col-md-10 d-flex flex-column justify-content-between p-4">
                                    <div>
                                        <h6 className="card-title fs-5 mb-1 one-line-text-ellipsis">{item.itemName}</h6>
                                        <p className="card-text mb-1">{formatPrice(item.price)}</p>
                                        <p className='card-text mb-1'>Category: {getCategoryNameById(item.categoryId)}</p>
                                        <p className={`card-text text-muted  mt-auto mb-0 ${styles.itemsDescription}`}>
                                            {item.description}
                                        </p>

                                        <p>{item.listed ? 'Listed  •  ' : ''} {item.views} views on this item</p>
                                        {item.featured && <h6 className='m-0'><span className="badge bg-success">Featured</span></h6>}
                                    </div>
                                    <div className="text-end mt-auto d-flex gap-2 justify-content-end">
                                        <button
                                            className="btn btn-sm btn-danger rounded-0 me-3"
                                            style={{ minWidth: '80px' }}
                                            onClick={() => handleDeleteItem(item.itemId)}
                                            disabled={item.itemId === itemIdToDelete && isDeleting}
                                        >
                                            {item.itemId === itemIdToDelete && isDeleting ? (<><span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span> Deleting ...</>) : ("Delete")}
                                        </button>
                                        <Link
                                            to={`/items/view/${item.itemId}`}
                                            state={{ item: undefined, categories }}
                                            className="btn btn-sm btn-outline-dark rounded-0"
                                            style={{ minWidth: '80px' }}
                                        >
                                            View
                                        </Link>
                                    </div>
                                </div>
                                {serviceError && <div className="col-md-12 clearfix"><div className="alert alert-danger p-2 mb-0">{serviceError}</div></div>}
                            </div>
                        </div>
                    ))}

                </div>
                {/* Pagination */}
                {!isLoading && items.length > 0 && (
                    <PaginationComp pageNum={pageNum} pageSize={pageSize} totalCount={totalItems} onPageChange={changePage} />
                )}
                <ConfirmDialogueComp
                    show={confirmDeleteModalVisible}
                    title="Confirm"
                    message="Are you sure you want to delete this item?"
                    onConfirm={handleDelete}
                    onCancel={handleCancel}
                />
            </div>
        </div>
    );
};

export default ItemsPage;
